import { defineStore } from 'pinia';
import tasksApi from '@/api/tasksApi';

export const useTasksStore = defineStore('tasks', {
    state: () => ({
        items: [],
        loading: false,
        error: null,
        details: new Map(),
        loadingIds: new Set(),
        page: { count: 0, next: null, previous: null },
    }),

    getters: {
        taskById: (s) => (id) => s.details.get(Number(id)) || s.items.find(t => t.id === Number(id)) || null,
    },

    actions: {
        async list(params = {}) {
            this.loading = true;
            this.error = null;
            try {
                const data = await tasksApi.getTasks(params);
                if (data && Array.isArray(data.results)) {
                    // Ответ с пагинацией
                    this.items = data.results;
                    this.page = { count: data.count ?? 0, next: data.next ?? null, previous: data.previous ?? null };
                } else if (Array.isArray(data)) {
                    // Ответ без пагинации (простой массив)
                    this.items = data;
                    this.page = { count: data.length, next: null, previous: null };
                } else {
                    this.items = [];
                    this.page = { count: 0, next: null, previous: null };
                }
            } catch (e) {
                this.error = e;
                throw e;
            } finally {
                this.loading = false;
            }
        },

        async listNext() {
            const nextUrl = this.page?.next;
            if (!nextUrl) return null;
            try {
                const data = await tasksApi.getUrl(nextUrl);
                const more = Array.isArray(data?.results) ? data.results : [];
                this.items = this.items.concat(more);
                this.page = { count: data.count ?? this.page.count, next: data.next ?? null, previous: data.previous ?? null };
                return more;
            } catch (e) {
                this.error = e;
                throw e;
            }
        },

        resetList() {
            this.items = [];
            this.page = { count: 0, next: null, previous: null };
            this.error = null;
        },

        /**
         * Применяет WebSocket событие к состоянию задач
         * @param {object} payload - WebSocket payload
         * @returns {void}
         */

        async loadOne(id) {
            const num = Number(id);
            if (!Number.isFinite(num)) return null;
            this.loadingIds.add(num);
            try {
                const data = await tasksApi.getTask(num);
                this.details.set(num, data);
                return data;
            } finally {
                this.loadingIds.delete(num);
            }
        },

        async create(payload) {
            const data = await tasksApi.createTask(payload);
            this._upsertDetailAndList(data);
            return data;
        },

        async done(id) {
            const data = await tasksApi.postDone(id);
            this._upsertDetailAndList(data);
            return data;
        },
        async decline(id, reason) {
            const data = await tasksApi.postDecline(id, reason);
            this._upsertDetailAndList(data);
            return data;
        },
        async returnTask(id, reason) {
            const data = await tasksApi.postReturn(id, reason);
            this._upsertDetailAndList(data);
            return data;
        },
        async close(id) {
            const data = await tasksApi.postClose(id);
            this._upsertDetailAndList(data);
            return data;
        },

        /**
         * Применяет WebSocket событие к состоянию задач
         * @param {object} payload - WebSocket payload
         * @returns {void}
         */

        applyWsEvent(payload) {
            if (!payload || payload.type !== 'task') return;
            const id = Number(payload.task_id);
            if (!Number.isFinite(id)) return;

            const idx = this.items.findIndex(t => t.id === id);
            if (idx !== -1) {
                this.items[idx] = { ...this.items[idx], status: payload.status ?? this.items[idx].status, title: payload.title ?? this.items[idx].title };
            }
            if (this.details.has(id)) {
                const old = this.details.get(id);
                this.details.set(id, { ...old, status: payload.status ?? old.status });
            }
        },

        _upsertDetailAndList(task) {
            if (!task?.id) return;
            const id = Number(task.id);
            this.details.set(id, task);

            const idx = this.items.findIndex(t => t.id === id);
            if (idx === -1) {
                this.items.unshift(task);
            } else {
                this.items[idx] = { ...this.items[idx], ...task };
            }
        },
    },
});